import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SubjectIllustration from './SubjectIllustration';
import { homeworkIllustration } from './subjectIllustrations';
import './teacherHomework.css';

const statuses = [
  ['done', 'Tam'], ['missing', 'Eksik'], ['not_done', 'Yok'], ['unrecorded', 'Değerlendirilmedi'],
];
const validCount = value => Number.isInteger(value) && value >= 0;

function dateLabel(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return value || '—';
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('tr-TR', {
    timeZone: 'Europe/Istanbul', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function HomeworkDescription({ text, titleId }) {
  const id = useId();
  const paragraph = useRef(null);
  const toggle = useRef(null);
  const keepToggleVisible = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  useLayoutEffect(() => {
    const node = paragraph.current;
    let active = true;
    const measure = () => {
      if (!active) return;
      // scrollHeight includes the text hidden by the three-line CSS clamp.
      // The same measurement also works while expanded and after width/font changes.
      const needed = node.scrollHeight > parseFloat(getComputedStyle(node).lineHeight) * 3 + 1;
      setCanExpand(needed);
      if (!needed) setExpanded(false);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [text]);

  useLayoutEffect(() => {
    // Keep the focused collapse control reachable when a tall paragraph shrinks.
    if (!expanded && keepToggleVisible.current) {
      toggle.current?.scrollIntoView?.({ block: 'nearest', behavior: 'instant' });
      keepToggleVisible.current = false;
    }
  }, [expanded]);

  return <div className="teacher-homework-description-block">
    <p id={id} ref={paragraph} className={`teacher-homework-description ${expanded ? 'is-expanded' : 'is-collapsed'}`}>{text}</p>
    {canExpand && <Button ref={toggle} type="button" className="teacher-homework-description-toggle"
      aria-expanded={expanded} aria-controls={id} aria-describedby={titleId}
      onClick={() => { keepToggleVisible.current = expanded; setExpanded(value => !value); }}>
      {expanded ? 'Daha Az Göster' : 'Devamını Göster'}
    </Button>}
  </div>;
}

function HomeworkCard({ homework, onOpenHomework }) {
  const id = useId();
  const illustration = homeworkIllustration(homework.lessons);
  const counts = homework.statusCounts || {};
  const completeCounts = statuses.every(([key]) => validCount(counts[key]));
  const action = completeCounts && counts.unrecorded === 0 ? 'view' : 'evaluate';
  const actionLabel = action === 'view' ? 'Durumları Gör' : 'Değerlendir';
  return <Paper component="article" aria-labelledby={id} elevation={0} className="teacher-homework-card"
    style={{ '--subject-accent': illustration.accent, '--subject-background': illustration.background }}>
    <div className="teacher-homework-content">
      <div className="teacher-homework-heading">
      <div className="teacher-homework-art-frame"><SubjectIllustration kind={illustration.kind} /></div>
      <div className="teacher-homework-heading-text">
        <p className="teacher-homework-subject">{(homework.lessons || []).map(lesson => lesson.name).filter(Boolean).join(' · ') || 'Ders belirtilmedi'}</p>
        <Typography component="h2" id={id} className="teacher-homework-title">{homework.title}</Typography>
        <p className="teacher-homework-dates"><CalendarTodayRoundedIcon aria-hidden="true" />
          <span>{dateLabel(homework.assignedDate)} – {dateLabel(homework.dueDate)}</span>
        </p>
      </div>
      </div>
      <HomeworkDescription key={homework.description} text={homework.description} titleId={id} />
    </div>
    <dl className="teacher-homework-counts" aria-label="Öğrenci durumları">
      {statuses.map(([key, label]) => <div key={key} className={`teacher-homework-count is-${key}`}>
        <dt>{label}</dt><dd>{validCount(counts[key]) ? counts[key] : '—'}</dd>
      </div>)}
    </dl>
    <div className="teacher-homework-footer">
      <span className="teacher-homework-students">{validCount(homework.studentCount) ? `${homework.studentCount} öğrenci` : ''}</span>
      <Button variant={action === 'view' ? 'outlined' : 'contained'} disableElevation endIcon={<ArrowForwardRoundedIcon />}
        disabled={!onOpenHomework || !completeCounts} onClick={() => onOpenHomework(homework.id, action)}
        aria-label={`${actionLabel}: ${homework.title}`} className="teacher-homework-cta">{actionLabel}</Button>
    </div>
  </Paper>;
}

// Data-only view: no auth, API, fixture imports or student-status mutation.
// The future PIN-session container supplies the selected class and bucket data.
export default function TeacherHomeworkList({ classCode, homeworks = [], bucket = 'active', onBucketChange,
  onOpenHomework, onBack, onQuick, loading = false, error = '', today }) {
  const id = useId();
  return <Box component="main" className="teacher-homework-list">
    {onBack && <Button onClick={onBack} startIcon={<ArrowBackRoundedIcon />} className="teacher-homework-back">Öğretmen Modu</Button>}
    <div className="teacher-homework-hero">
      <div><Typography component="h1" className="teacher-homework-page-title"><FactCheckRoundedIcon aria-hidden="true" />Ödev Değerlendir</Typography>
        <Typography component="p" className="teacher-homework-subtitle">{classCode} Sınıfı · Değerlendirmek istediğiniz ödevi seçin.</Typography>
      </div>
      {today && <span className="teacher-homework-today">{dateLabel(today)}</span>}
    </div>
    <div className="teacher-homework-tabs">
      <Tabs value={bucket} onChange={(_, value) => onBucketChange?.(value)} aria-label="Ödev grubu">
        <Tab id={`${id}-active`} aria-controls={`${id}-panel`} value="active" label="Aktif" />
        <Tab id={`${id}-archived`} aria-controls={`${id}-panel`} value="archived" label="Arşiv" />
      </Tabs>
      {onQuick && <Button variant="contained" disableElevation onClick={onQuick} className="teacher-homework-quick">⚡ Hızlı Değerlendir</Button>}
    </div>
    <section id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-${bucket}`} aria-busy={loading}>
      {error ? <Alert severity="error">{error}</Alert> : loading ? <Box role="status" className="teacher-homework-empty"><CircularProgress size={28} /><span>Ödevler yükleniyor…</span></Box>
        : homeworks.length ? <div className="teacher-homework-grid">{homeworks.map(homework => <HomeworkCard key={homework.id} homework={homework} onOpenHomework={onOpenHomework} />)}</div>
          : <Paper elevation={0} className="teacher-homework-empty">{bucket === 'archived' ? 'Arşivde henüz ödev yok.' : 'Henüz aktif ödev yok.'}</Paper>}
    </section>
  </Box>;
}
